import {
  Card,
  Center,
  Group,
  Loader,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ListingCard } from '../components/ListingCard.js';
import { RejectModal } from '../components/RejectModal.js';
import { SearchProfileFilter } from '../components/SearchProfileFilter.js';
import { ShareModal } from '../components/ShareModal.js';
import {
  listObjects,
  rejectObject,
  shortlistObject,
  type ObjectView,
  type RejectCategory,
} from '../lib/api.js';

type StatusFilter = 'new' | 'partner_review' | 'match' | 'contacted' | 'rejected' | 'all';
type ScoreFilter = 'over_threshold' | 'all';

export function ObjectList(): JSX.Element {
  const [searchProfileId, setSearchProfileId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<StatusFilter>('new');
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter>('over_threshold');
  const qc = useQueryClient();

  const [rejectModal, setRejectModal] = useState<{ id: number; title: string } | null>(null);
  const [rejectCategory, setRejectCategory] = useState<RejectCategory | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [shareModal, setShareModal] = useState<{ id: number; text: string; title: string; url?: string } | null>(null);

  // Score-Filter gilt nur für die 'new'-Pipeline. Bereits einsortierte Phasen
  // (partner_review/contacted/rejected) immer komplett zeigen. „ripe" = über der
  // pro-Profil Swipe-Schwelle (identisch zur Swipe-Queue), nicht mehr flache 0,65.
  const ripe = status === 'new' && scoreFilter === 'over_threshold';

  const { data, isLoading, error } = useQuery({
    queryKey: ['objects', searchProfileId, status, ripe],
    queryFn: () =>
      listObjects({
        searchProfileId,
        status: status === 'all' ? undefined : status,
        ripe,
      }),
    refetchInterval: 30_000,
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, category, note }: { id: number; category: RejectCategory; note: string }) =>
      rejectObject(id, category, note || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['objects'] });
      qc.invalidateQueries({ queryKey: ['swipe-queue'] });
      setRejectModal(null);
      setRejectCategory(null);
      setRejectNote('');
      notifications.show({ message: '✗ Pass gesetzt', color: 'red' });
    },
    onError: (err) =>
      notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const shortlistMut = useMutation({
    mutationFn: (obj: ObjectView) => shortlistObject(obj.id),
    onSuccess: (res, obj) => {
      qc.invalidateQueries({ queryKey: ['objects'] });
      qc.invalidateQueries({ queryKey: ['swipe-queue'] });
      setShareModal({
        id: obj.id,
        text: res.whatsappText,
        title: obj.listing?.title ?? 'Inserat',
        url: obj.listing?.url ?? undefined,
      });
    },
    onError: (err) =>
      notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const stats = useMemo(() => {
    if (!data) return null;
    const obs = data.objects;
    return {
      // total = ungekappte Gesamtzahl (Backend liefert nur die erste Seite in `objects`).
      total: data.totalCount ?? obs.length,
      totalUnfiltered: data.totalCountUnfiltered ?? obs.length,
      live: obs.filter((o) => (o.score?.killedBy ?? null) === null).length,
    };
  }, [data]);

  const submitReject = (): void => {
    if (!rejectModal || !rejectCategory) return;
    rejectMut.mutate({ id: rejectModal.id, category: rejectCategory, note: rejectNote });
  };

  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Title order={1}>Liste</Title>
        <Text c="dimmed" size="sm">
          {stats === null
            ? 'Lade…'
            : status === 'new'
              ? scoreFilter === 'over_threshold'
                ? `${stats.total} von ${stats.totalUnfiltered} über der Swipe-Schwelle (reif zum Swipen).`
                : `${stats.total} Objekte in der Pipeline · ${stats.live} nicht gekillt.`
              : `${stats.total} Objekt${stats.total === 1 ? '' : 'e'} in dieser Phase.`}
        </Text>
      </Stack>

      <Group justify="flex-start" gap="md" wrap="wrap">
        <Stack gap={4}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.05em' }}>
            Profil
          </Text>
          <SearchProfileFilter value={searchProfileId} onChange={setSearchProfileId} />
        </Stack>
        <Stack gap={4}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.05em' }}>
            Status
          </Text>
          <SegmentedControl
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            data={[
              { label: 'Pipeline', value: 'new' },
              { label: '🤝 Partner', value: 'partner_review' },
              { label: '🎯 Match', value: 'match' },
              { label: '✓ In Kontakt', value: 'contacted' },
              { label: '✗ Abgelehnt', value: 'rejected' },
              { label: 'Alle', value: 'all' },
            ]}
            size="sm"
            radius="md"
          />
        </Stack>
        <Stack gap={4}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.05em' }}>
            Score
          </Text>
          <SegmentedControl
            value={scoreFilter}
            onChange={(v) => setScoreFilter(v as ScoreFilter)}
            data={[
              { label: 'Swipe-reif', value: 'over_threshold' },
              { label: 'Alle', value: 'all' },
            ]}
            size="sm"
            radius="md"
          />
        </Stack>
      </Group>

      {isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      {error && (
        <Card>
          <Text c="red.7">Fehler beim Laden: {String(error)}</Text>
        </Card>
      )}

      {data && data.objects.length === 0 && (
        <Card>
          <Center py="lg">
            <Stack align="center" gap={6}>
              <Text size="lg" fw={500}>
                Keine Treffer
              </Text>
              <Text c="dimmed" size="sm">
                Ändere die Filter oder warte auf neue Inserate.
              </Text>
            </Stack>
          </Center>
        </Card>
      )}

      {data && data.objects.length > 0 && (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {data.objects.map((o) => {
            // Swipe-Aktionen nur für Pipeline-Objekte ('new'). Bereits einsortierte
            // (partner_review/contacted) werden in ihren eigenen Tabs verwaltet.
            const canReject = o.status !== 'rejected';
            const canShortlist = o.status === 'new';
            return (
              <ListingCard
                key={o.id}
                obj={o}
                onSwipeLeft={
                  canReject
                    ? () => setRejectModal({ id: o.id, title: o.listing?.title ?? 'Inserat' })
                    : undefined
                }
                onSwipeRight={canShortlist ? () => shortlistMut.mutate(o) : undefined}
              />
            );
          })}
        </SimpleGrid>
      )}

      {data && (
        <Text size="xs" c="dimmed" ta="center">
          {data.objects.length} von {data.totalCount} Objekten angezeigt
        </Text>
      )}

      <RejectModal
        opened={rejectModal !== null}
        onClose={() => {
          setRejectModal(null);
          setRejectCategory(null);
          setRejectNote('');
        }}
        note={rejectNote}
        onNoteChange={setRejectNote}
        onPick={(category) => {
          if (rejectModal) rejectMut.mutate({ id: rejectModal.id, category, note: rejectNote });
        }}
        pending={rejectMut.isPending}
      />

      <ShareModal
        opened={shareModal !== null}
        onClose={() => setShareModal(null)}
        text={shareModal?.text ?? ''}
        title={shareModal?.title ?? ''}
        listingUrl={shareModal?.url}
      />
    </Stack>
  );
}
